import { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { features as setFeature } from '../redux/slices/Tree/treeFeatures'
import { setMapDataReady } from '../redux/slices/Welcome/welcomeSlice'
import { getAllLocations } from '../services'

/**
 * Carga todos los árboles del cantón actual desde el backend y los guarda
 * en Redux.
 * Se recarga cuando cambia el contexto (cantón + tenant) y no hay filtros activos.
 *
 * @returns {{ loading: boolean }}
 */
export const useLoadMapData = ({
  map,
  mapLoaded,
  layersReady,
  currentCity,
  selectedTenant,
  isUsingFilters,
  alertRef,
  isEditMode = false,
}) => {
  const dispatch = useDispatch()
  const reloadVersion = useSelector(state => state.treeFeatures.reloadVersion)
  const [loading, setLoading] = useState(false)
  const lastLoadedContextRef = useRef(null)
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!mapLoaded || !map || !layersReady) return
    if (!currentCity) return
    if (isUsingFilters) return

    const contextKey = `${currentCity}::${selectedTenant || '__all__'}::${reloadVersion}`
    const contextChanged = lastLoadedContextRef.current !== contextKey

    // Evita recargas innecesarias para el mismo contexto (cantón + tenant)
    if (!contextChanged) return

    const requestId = ++requestIdRef.current
    let cancelled = false
    setLoading(true)

    getAllLocations(currentCity, selectedTenant || null)
      .then(geojson => {
        if (cancelled || requestId !== requestIdRef.current) return
        lastLoadedContextRef.current = contextKey
        setLoading(false)

        dispatch(setFeature(geojson || { type: 'FeatureCollection', features: [] }))
        dispatch(setMapDataReady())

        if (!geojson?.features?.length && !isEditMode) {
          alertRef?.current?.message('No existe registros de árboles en la zona.')
          alertRef?.current?.isSeverity('info')
          alertRef?.current?.openAlert(true)
        }
      })
      .catch(err => {
        if (cancelled || requestId !== requestIdRef.current) return
        setLoading(false)
        console.error('[useLoadMapData] Error cargando datos:', err)
        alertRef?.current?.message('No fue posible al cargar los árboles.')
        alertRef?.current?.isSeverity('warning')
        alertRef?.current?.openAlert(true)
      })

    return () => {
      cancelled = true
      // Si este efecto inició una carga y fue cancelado antes de completar,
      // resetear loading para evitar que quede atascado en true
      setLoading(false)
    }
  }, [
    mapLoaded,
    map,
    layersReady,
    dispatch,
    isUsingFilters,
    alertRef,
    currentCity,
    selectedTenant,
    reloadVersion,
    isEditMode,
  ])

  return { loading }
}
